import { useState, useEffect } from 'react'
import { datedName, downloadCsv, toCsv } from '../utils/csv'
import {
  Shield, Clock, FileText, AlertTriangle, Award,
  Search, Download, ChevronRight, CheckCircle, XCircle,
} from 'lucide-react'
import { portfoliosService, Portfolio } from '../services/portfolios'
import {
  portfolioGovernanceService,
  type PortfolioGovernanceOverview,
  type PortfolioGovernanceStageGate,
  type PortfolioGovernanceAuditEntry,
} from '../services/portfolioGovernance'
import { toast } from 'sonner'
import { getErrorMessage } from '../utils/apiTransformers'
import { HeroSection } from '../components/HeroSection'

// The legacy page rendered fictional "Frameworks / Compliance Areas /
// Policies" arrays as if they were live data. None of those concepts map to
// a real table. This rewrite uses only the data we actually have:
//   * Stage gates aggregated from project_approval_gates
//   * Audit log entries from audit_log (governance-sla)
//   * Compliance signal = % of governed projects with all gates approved
// Tabs that have no backing data are gone; if you want frameworks / policies,
// they'll need their own tables first.

type TabId = 'overview' | 'gates' | 'audit'

function fmtDate(iso: string | null): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  } catch {
    return iso
  }
}

function gateStatusBadge(status: string): string {
  const s = status.toLowerCase()
  if (s === 'approved' || s === 'completed') return 'bg-green-100 text-green-700 border-green-200'
  if (s === 'rejected') return 'bg-red-100 text-red-700 border-red-200'
  if (s === 'in-progress' || s === 'in_progress' || s === 'under_review') return 'bg-yellow-100 text-yellow-700 border-yellow-200'
  return 'bg-blue-100 text-blue-700 border-blue-200'
}

export default function PortfolioGovernance() {
  const [loading, setLoading] = useState(true)
  const [overview, setOverview] = useState<PortfolioGovernanceOverview | null>(null)
  const [portfolios, setPortfolios] = useState<Portfolio[]>([])
  const [selectedPortfolio, setSelectedPortfolio] = useState<number | 'all'>('all')
  const [activeTab, setActiveTab] = useState<TabId>('overview')
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    Promise.all([
      portfoliosService.list().catch(() => [] as Portfolio[]),
      portfolioGovernanceService
        .overview(selectedPortfolio === 'all' ? undefined : selectedPortfolio)
        .catch((err) => {
          toast.error(getErrorMessage(err) || 'Failed to load governance data')
          return null
        }),
    ])
      .then(([pfs, ov]) => {
        if (cancelled) return
        setPortfolios(pfs ?? [])
        setOverview(ov)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [selectedPortfolio])

  const metrics = overview?.metrics
  const stageGates = overview?.stage_gates ?? []
  const auditLog = overview?.audit_log ?? []

  const filteredGates = searchQuery.trim()
    ? stageGates.filter((g) => {
        const q = searchQuery.toLowerCase()
        return (
          g.name.toLowerCase().includes(q) ||
          g.project_name.toLowerCase().includes(q) ||
          (g.portfolio_name ?? '').toLowerCase().includes(q) ||
          g.gate_number.toLowerCase().includes(q)
        )
      })
    : stageGates

  const exportGates = () => {
    if (filteredGates.length === 0) {
      toast.info('No stage gates to export.')
      return
    }
    downloadCsv(datedName('governance-stage-gates'), toCsv(filteredGates, [
      { label: 'Project', value: (g) => g.project_name },
      { label: 'Portfolio', value: (g) => g.portfolio_name },
      { label: 'Gate', value: (g) => g.gate_number },
      { label: 'Name', value: (g) => g.name },
      { label: 'Status', value: (g) => g.status },
      { label: 'Progress %', value: (g) => g.progress },
      { label: 'Due', value: (g) => g.due_date },
      { label: 'Completed', value: (g) => g.completed_date },
      { label: 'Approver', value: (g) => g.approver },
      { label: 'Risk', value: (g) => g.risk_status },
      { label: 'Documents done', value: (g) => `${g.documents.completed}/${g.documents.total}` },
      { label: 'Decisions approved', value: (g) => `${g.decisions.approved}/${g.decisions.total}` },
    ]))
  }

  // Hero metric tiles — all derived from real numbers. No invented deltas.
  const heroMetrics = [
    {
      label: 'Compliance Rate',
      value: metrics?.compliance_rate == null ? '—' : `${metrics.compliance_rate}%`,
      sublabel: metrics ? `${metrics.governed_projects} governed projects` : '',
      icon: Shield,
      iconColor: 'bg-green-50',
    },
    {
      label: 'Pending Gates',
      value: (metrics?.pending_gates ?? 0).toString(),
      sublabel: metrics?.overdue_gates ? `${metrics.overdue_gates} overdue` : 'On schedule',
      icon: Clock,
      iconColor: 'bg-yellow-50',
    },
    {
      label: 'Completed Gates',
      value: (metrics?.completed_gates ?? 0).toString(),
      sublabel: metrics ? `of ${metrics.stage_gate_count} total` : '',
      icon: Award,
      iconColor: 'bg-blue-50',
    },
    {
      label: 'Audit Entries',
      value: auditLog.length.toString(),
      sublabel: 'Recent activity',
      icon: AlertTriangle,
      iconColor: auditLog.length > 0 ? 'bg-purple-50' : 'bg-gray-50',
    },
  ]

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading governance data...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-8">
      <div className="max-w-[1800px] mx-auto space-y-6">
        <HeroSection
          title="Governance"
          description="Stage-gate oversight and audit trail across portfolios, programs, and projects"
          variant="level1"
          metrics={heroMetrics}
          actionButton={{
            label: 'Export Report',
            onClick: exportGates,
            icon: <Download className="w-5 h-5" />,
          }}
        />

        {/* Filters + tabs */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <select
                value={selectedPortfolio}
                onChange={(e) =>
                  setSelectedPortfolio(e.target.value === 'all' ? 'all' : parseInt(e.target.value))
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900"
              >
                <option value="all">All Portfolios</option>
                {portfolios.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex gap-2 border-l border-gray-200 pl-4">
              {(['overview', 'gates', 'audit'] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => setActiveTab(t)}
                  className={`px-4 py-2 rounded-lg text-sm transition-colors capitalize ${
                    activeTab === t
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {t === 'audit' ? 'Audit Log' : t === 'gates' ? 'Stage Gates' : 'Overview'}
                </button>
              ))}
            </div>
          </div>
        </div>

        {activeTab === 'overview' && (
          <OverviewTab
            stageGates={stageGates.slice(0, 6)}
            auditLog={auditLog.slice(0, 6)}
            onSeeAllGates={() => setActiveTab('gates')}
            onSeeAllAudit={() => setActiveTab('audit')}
          />
        )}

        {activeTab === 'gates' && (
          <StageGatesTab
            stageGates={filteredGates}
            searchQuery={searchQuery}
            onSearch={setSearchQuery}
          />
        )}

        {activeTab === 'audit' && <AuditLogTab auditLog={auditLog} />}
      </div>
    </div>
  )
}

function OverviewTab({
  stageGates,
  auditLog,
  onSeeAllGates,
  onSeeAllAudit,
}: {
  stageGates: PortfolioGovernanceStageGate[]
  auditLog: PortfolioGovernanceAuditEntry[]
  onSeeAllGates: () => void
  onSeeAllAudit: () => void
}) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Upcoming gates */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Upcoming Stage Gates</h3>
          <button
            onClick={onSeeAllGates}
            className="text-sm text-blue-600 hover:text-blue-700 inline-flex items-center gap-1"
          >
            View all <ChevronRight className="w-3 h-3" />
          </button>
        </div>
        {stageGates.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Shield className="w-10 h-10 mx-auto mb-2 text-gray-300" />
            <p className="text-sm">No stage gates yet. Add gates to a project to track approvals here.</p>
          </div>
        ) : (
          <ul className="space-y-2">
            {stageGates.map((g) => (
              <li key={g.id} className="flex items-center justify-between p-3 border border-gray-100 rounded-lg hover:bg-gray-50">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-medium text-gray-500">{g.gate_number}</span>
                    <span className="text-sm font-medium text-gray-900 truncate">{g.name}</span>
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    {g.portfolio_name ? `${g.portfolio_name} · ` : ''}
                    {g.project_name}
                  </div>
                </div>
                <div className="text-right flex-shrink-0 ml-3">
                  <span className={`px-2 py-0.5 rounded text-xs border font-medium capitalize ${gateStatusBadge(g.status)}`}>
                    {g.status.replace('_', ' ')}
                  </span>
                  <div className="text-xs text-gray-500 mt-1">Due {fmtDate(g.due_date)}</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Recent audit */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Recent Audit Activity</h3>
          <button
            onClick={onSeeAllAudit}
            className="text-sm text-blue-600 hover:text-blue-700 inline-flex items-center gap-1"
          >
            View all <ChevronRight className="w-3 h-3" />
          </button>
        </div>
        {auditLog.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <FileText className="w-10 h-10 mx-auto mb-2 text-gray-300" />
            <p className="text-sm">No audit entries yet. Activity from governance reviews shows up here.</p>
          </div>
        ) : (
          <ul className="space-y-2">
            {auditLog.map((a) => (
              <li key={a.id} className="p-3 border border-gray-100 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-medium text-gray-700">{a.action}</span>
                  <span className="text-xs text-gray-500">·</span>
                  <span className="text-xs text-gray-500 capitalize">{a.entity_type.replace('_', ' ')}</span>
                </div>
                {a.description && (
                  <div className="text-xs text-gray-700 truncate">{a.description}</div>
                )}
                <div className="text-xs text-gray-400 mt-1">{fmtDate(a.created_at)}</div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

function StageGatesTab({
  stageGates,
  searchQuery,
  onSearch,
}: {
  stageGates: PortfolioGovernanceStageGate[]
  searchQuery: string
  onSearch: (q: string) => void
}) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
      <div className="p-4 border-b border-gray-200 flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Search by gate name, project, or portfolio…"
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="text-sm text-gray-500">{stageGates.length} gate{stageGates.length === 1 ? '' : 's'}</div>
      </div>
      {stageGates.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Shield className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="text-sm">No stage gates match the current filter.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs uppercase tracking-wider text-gray-600">Gate</th>
                <th className="px-6 py-3 text-left text-xs uppercase tracking-wider text-gray-600">Project</th>
                <th className="px-6 py-3 text-left text-xs uppercase tracking-wider text-gray-600">Portfolio</th>
                <th className="px-6 py-3 text-left text-xs uppercase tracking-wider text-gray-600">Status</th>
                <th className="px-6 py-3 text-left text-xs uppercase tracking-wider text-gray-600">Progress</th>
                <th className="px-6 py-3 text-left text-xs uppercase tracking-wider text-gray-600">Due</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {stageGates.map((g) => (
                <tr key={g.id} className="hover:bg-gray-50">
                  <td className="px-6 py-3 text-sm">
                    <div className="flex items-center gap-2">
                      {g.status === 'completed' || g.status === 'approved' ? (
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      ) : g.status === 'rejected' ? (
                        <XCircle className="w-4 h-4 text-red-600" />
                      ) : (
                        <Clock className="w-4 h-4 text-yellow-600" />
                      )}
                      <span className="font-medium text-gray-900">
                        {g.gate_number} · {g.name}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-3 text-sm text-gray-700">{g.project_name}</td>
                  <td className="px-6 py-3 text-sm text-gray-500">{g.portfolio_name ?? '—'}</td>
                  <td className="px-6 py-3 text-sm">
                    <span className={`px-2 py-0.5 rounded text-xs border font-medium capitalize ${gateStatusBadge(g.status)}`}>
                      {g.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-6 py-3 text-sm text-gray-700">
                    <div className="flex items-center gap-2">
                      <div className="w-24 bg-gray-200 rounded-full h-1.5">
                        <div className="bg-blue-600 h-1.5 rounded-full" style={{ width: `${g.progress}%` }} />
                      </div>
                      <span className="text-xs text-gray-500">{g.progress}%</span>
                    </div>
                  </td>
                  <td className="px-6 py-3 text-sm text-gray-700">{fmtDate(g.due_date)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function AuditLogTab({ auditLog }: { auditLog: PortfolioGovernanceAuditEntry[] }) {
  if (auditLog.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-12 text-center text-gray-500">
        <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
        <p className="text-sm mb-1">No audit entries yet.</p>
        <p className="text-xs">Governance reviews, escalations, and policy decisions show up here.</p>
      </div>
    )
  }
  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-3 text-left text-xs uppercase tracking-wider text-gray-600">When</th>
              <th className="px-6 py-3 text-left text-xs uppercase tracking-wider text-gray-600">Action</th>
              <th className="px-6 py-3 text-left text-xs uppercase tracking-wider text-gray-600">Entity</th>
              <th className="px-6 py-3 text-left text-xs uppercase tracking-wider text-gray-600">Description</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {auditLog.map((a) => (
              <tr key={a.id} className="hover:bg-gray-50">
                <td className="px-6 py-3 text-sm text-gray-500 whitespace-nowrap">{fmtDate(a.created_at)}</td>
                <td className="px-6 py-3 text-sm font-medium text-gray-900">{a.action}</td>
                <td className="px-6 py-3 text-sm text-gray-700 capitalize">
                  {a.entity_type.replace('_', ' ')}
                  {a.entity_id !== null ? ` #${a.entity_id}` : ''}
                </td>
                <td className="px-6 py-3 text-sm text-gray-700">{a.description ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
